import {
  faceDetectionService,
  type FaceDetectionResult,
} from '../services/FaceDetectionService';

/**
 * Pixel rectangle for skin / undertone sampling (image coordinates).
 */
export type SkinAnalysisRegion = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Clamp a region so it lies fully inside the image with positive size.
 */
export function clampSkinAnalysisRegion(
  region: SkinAnalysisRegion,
  imageWidth: number,
  imageHeight: number
): SkinAnalysisRegion {
  const x = Math.max(
    0,
    Math.min(Math.floor(region.x), Math.max(0, imageWidth - 1))
  );
  const y = Math.max(
    0,
    Math.min(Math.floor(region.y), Math.max(0, imageHeight - 1))
  );
  const width = Math.max(1, Math.min(Math.ceil(region.width), imageWidth - x));
  const height = Math.max(
    1,
    Math.min(Math.ceil(region.height), imageHeight - y)
  );
  return { x, y, width, height };
}

/**
 * Expand a face bounding box slightly for undertone sampling (cheeks, jaw).
 */
export function faceBoxToAnalysisRegion(
  box: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
  paddingRatio = 0.2
): SkinAnalysisRegion {
  const pad = Math.max(box.width, box.height) * paddingRatio;
  return clampSkinAnalysisRegion(
    {
      x: box.x - pad,
      y: box.y - pad,
      width: box.width + 2 * pad,
      height: box.height + 2 * pad,
    },
    imageWidth,
    imageHeight
  );
}

/**
 * Run face detection once; returns padded ROI for undertone (if confident face)
 * and the raw result for UI / cache.
 */
export async function resolveFaceAnalysisForUndertone(
  imageData: ImageData
): Promise<{
  region: SkinAnalysisRegion | undefined;
  faceResult: FaceDetectionResult;
}> {
  try {
    if (!faceDetectionService.isReady()) {
      await faceDetectionService.initialize();
    }
    const faceResult = await faceDetectionService.detectFace(imageData);
    let region: SkinAnalysisRegion | undefined;
    if (
      faceResult.detected &&
      faceResult.boundingBox &&
      faceResult.confidence > 0.5
    ) {
      region = faceBoxToAnalysisRegion(
        faceResult.boundingBox,
        imageData.width,
        imageData.height
      );
    }
    return { region, faceResult };
  } catch {
    return {
      region: undefined,
      faceResult: { detected: false, confidence: 0 },
    };
  }
}

/** ROI only — use {@link resolveFaceAnalysisForUndertone} when you need the full result. */
export async function resolveFaceRegionForSkinAnalysis(
  imageData: ImageData
): Promise<SkinAnalysisRegion | undefined> {
  const { region } = await resolveFaceAnalysisForUndertone(imageData);
  return region;
}
